import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HyperlinkText, useTextToHyperlink } from './TextToHyperlink';
import { buildPersonDetailsUrl } from '../utils/personDetailsNavigation';

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    .visitor-name-link {
      color: inherit !important;
      text-decoration: none !important;
      cursor: pointer;
    }
    .visitor-name-link:hover {
      color: #000FFF !important;
      text-decoration: underline !important;
    }
  `;
  document.head.appendChild(style);
}

const VisitorHyperlinkCore = ({
  text,
  persons = [],
  detailKind = 'visitor',
  linkClassName = 'visitor-name-link',
  ...props
}) => {
  const navigate = useNavigate();
  const { addListener, removeListener, instance, isReady } = useTextToHyperlink();
  const [forceUpdate, setForceUpdate] = React.useState(0);

  React.useEffect(() => {
    if (isReady) {
      setForceUpdate((prev) => prev + 1);
    }
  }, [isReady]);

  const navigateToPersonDetails = React.useCallback((personId) => {
    const url = buildPersonDetailsUrl(personId, { detailKind });
    if (url) {
      navigate(url);
    }
  }, [detailKind, navigate]);

  React.useEffect(() => {
    if (!isReady) return;

    persons.forEach((person) => {
      if (person.name) {
        removeListener(person.name);
      }
    });

    persons.forEach((person) => {
      if (person.name && person.person_id) {
        addListener(
          person.name,
          () => {
            navigateToPersonDetails(person.person_id);
          },
          {
            wholeWord: true,
            caseSensitive: true,
            className: linkClassName,
            style: {
              cursor: 'pointer',
              fontWeight: 'medium',
            },
          }
        );
      }
    });

    return () => {
      persons.forEach((person) => {
        if (person.name) {
          removeListener(person.name);
        }
      });
    };
  }, [persons, addListener, removeListener, isReady, forceUpdate, navigateToPersonDetails, linkClassName]);

  const handleClick = React.useCallback((e) => {
    if (!isReady || persons.length === 0) return;

    const person = persons[0];
    if (person?.name && person?.person_id) {
      e.preventDefault();
      navigateToPersonDetails(person.person_id);
    }
  }, [persons, isReady, navigateToPersonDetails]);

  if (!isReady) {
    return (
      <span
        {...props}
        onClick={handleClick}
        style={{
          cursor: persons.length > 0 ? 'pointer' : 'default',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        {text}
      </span>
    );
  }

  return (
    <HyperlinkText
      text={text}
      hyperlinkInstance={instance}
      fallbackProps={{
        onClick: handleClick,
      }}
      {...props}
    />
  );
};

export const VisitorNameHyperlink = ({ text, persons = [], ...props }) => (
  <VisitorHyperlinkCore text={text} persons={persons} detailKind="visitor" {...props} />
);

export default VisitorNameHyperlink;
